/**
 * The frame a 2D canvas draws through, the root's or a sub-viewport's. The camera maps the canvas
 * top to GL row 0 (`world2DCamera.ts`), so the frame holds it upside down and copies it, rows
 * reversed, to the target bound before it. It is multisampled when `msaa_2d` is on: Godot draws
 * into a buffer at the mode's sample count (`texture_storage.cpp:3604-3613`), which a drawing buffer cannot.
 */
import * as THREE from 'three';
import { copyTexels, reversedTexelRows } from '../../texelCopy';
import { takeOutputLikeDrawingBuffer } from '../../outputTarget';
import { beginStep, endStep } from '../../frameProfile/frameProfile';
/**
 * Copies `frame` to the target, each texel to the pixel under it from the frame's row
 * `height - 1 - y`, so the copy neither filters nor converts and the drawing buffer's row 0 is the
 * canvas bottom again.
 */
function copyToTarget(gl, frame, target, transfer) {
    beginStep(gl, 'Canvas copy');
    try {
        copyTexels(gl, frame.texture, target, frame.rowsReversed(), transfer);
    }
    finally {
        endStep(gl, 'Canvas copy');
    }
}
/**
 * RGBA8 storage under an sRGB tag. Taking output like the drawing buffer, three encodes each
 * fragment to the tag's space (r187 `WebGLPrograms.js:217`), so the frame holds what the drawing buffer
 * would hold and items blend in the same space. The default sRGB format, SRGB8_ALPHA8, would blend
 * in linear.
 */
class CanvasFrame extends THREE.WebGLRenderTarget {
    outputSize = new THREE.Vector2();
    placement = reversedTexelRows(1, 1);
    constructor() {
        super(1, 1, { depthBuffer: true, stencilBuffer: false });
        this.texture.colorSpace = THREE.SRGBColorSpace;
        this.texture.internalFormat = 'RGBA8';
        this.texture.minFilter = THREE.NearestFilter;
        this.texture.magFilter = THREE.NearestFilter;
        this.texture.generateMipmaps = false;
        this.texture.name = 'Canvas2D::frame';
        takeOutputLikeDrawingBuffer(this);
    }
    /**
     * Draws `scene` into the frame and copies the result, rows reversed, to the target bound before.
     * The frame clears itself where the render will not: a render clears only while the renderer's
     * `autoClear` is on, which a caller may turn off. A target of SRGB8_ALPHA8 storage takes the
     * `decode` transfer, so its hardware encode writes the frame's bytes back unchanged.
     */
    render(gl, scene, camera, transfer = 'none') {
        const previous = gl.getRenderTarget();
        this.sizeLike(gl, previous);
        try {
            gl.setRenderTarget(this);
            if (!gl.autoClear)
                gl.clear(true, true, true);
            gl.render(scene, camera);
        }
        catch (error) {
            gl.setRenderTarget(previous);
            throw error;
        }
        copyToTarget(gl, this, previous, transfer);
    }
    /** The copy neither scales nor filters, so the frame is as large as the output it copies to. */
    sizeLike(gl, output) {
        if (output)
            this.outputSize.set(output.width, output.height);
        else
            gl.getDrawingBufferSize(this.outputSize);
        if (this.width !== this.outputSize.x || this.height !== this.outputSize.y) {
            this.setSize(this.outputSize.x, this.outputSize.y);
        }
    }
    /** Each row of the frame from its mirror row, made again only when the frame changes size. */
    rowsReversed() {
        const [, , width, height] = this.placement.bounds;
        if (width !== this.width || height !== this.height)
            this.placement = reversedTexelRows(this.width, this.height);
        return this.placement;
    }
}
/** The factory `useMultisampledTarget` takes for a 2D canvas frame. */
export const createCanvasFrame = () => new CanvasFrame();
//# sourceMappingURL=canvasFrame.js.map